import type { DiagramDocument, DiagramNode } from '@ukrop-nm/shared';
import { DEVICE_TYPE_LABELS_UK, formatLifecycleForDocument } from '@ukrop-nm/shared';

export type DrawingSheetMeta = {
  organization: string;
  projectName: string;
  objectName: string;
  drawingTitle: string;
  sheetCode: string;
  sheet: string;
  sheetsTotal: string;
  revision: string;
  author: string;
  checkedBy: string;
  date: string;
  scale: string;
  notes: string;
  /** Who the document is for (operators, admins, …). */
  purposeAudience: string;
  /** Why the document is produced. */
  purposeGoal: string;
  /** What is included / excluded on the scheme. */
  purposeScope: string;
};

import { accountStorageKey } from '../auth/accountScope';

function metaKey() {
  return accountStorageKey('drawing-meta');
}

export function defaultDrawingMeta(siteId: string, siteName: string): DrawingSheetMeta {
  const today = new Date().toISOString().slice(0, 10);
  return {
    organization: 'UKROP',
    projectName: siteName || siteId,
    objectName: siteName || siteId,
    drawingTitle: 'Схема логічної топології мережі',
    sheetCode: `СХ-${siteId}`,
    sheet: '1',
    sheetsTotal: '1',
    revision: 'A',
    author: 'Косенко Олексій',
    checkedBy: '',
    date: today,
    scale: 'без масштабу',
    notes: '',
    purposeAudience: 'адміністратори мережі та персонал впровадження / супроводу',
    purposeGoal:
      'зафіксувати склад обладнання, каналів звʼязку та адресного плану для впровадження і подальшого моніторингу',
    purposeScope: '',
  };
}

export function loadDrawingMeta(siteId: string, siteName: string): DrawingSheetMeta {
  const base = defaultDrawingMeta(siteId, siteName);
  try {
    const raw = localStorage.getItem(metaKey());
    if (!raw) return base;
    const all = JSON.parse(raw) as Record<string, Partial<DrawingSheetMeta>>;
    const saved = all[siteId];
    if (!saved || typeof saved !== 'object') return base;
    return { ...base, ...saved, objectName: saved.objectName || base.objectName };
  } catch {
    return base;
  }
}

export function saveDrawingMeta(siteId: string, meta: DrawingSheetMeta) {
  try {
    const raw = localStorage.getItem(metaKey());
    const all = raw ? (JSON.parse(raw) as Record<string, DrawingSheetMeta>) : {};
    all[siteId] = meta;
    localStorage.setItem(metaKey(), JSON.stringify(all));
  } catch {
    // ignore
  }
}

export function formatDrawingDateUk(isoDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!m) return isoDate.trim() || '—';
  return `${m[3]}.${m[2]}.${m[1]}`;
}

function uniqueCidrs(doc: DiagramDocument): string[] {
  const set = new Set<string>();
  for (const e of doc.edges) {
    const c = e.networkCidr?.trim();
    if (c) set.add(c);
  }
  return [...set].sort();
}

function typeSummary(devices: DiagramNode[]): string {
  const counts = new Map<string, number>();
  for (const n of devices) {
    const label = DEVICE_TYPE_LABELS_UK[n.type] ?? n.type;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label, n]) => `${label} — ${n}`)
    .join(', ');
}

/**
 * Rule-based «Призначення» from diagram facts + answers from the drawing dialog.
 * Locked nodes are included in counts (lock only freezes position in the editor).
 */
export function buildPurposeText(doc: DiagramDocument, meta: DrawingSheetMeta): string {
  const zones = doc.nodes.filter((n) => n.type === 'zone');
  const devices = doc.nodes.filter((n) => n.type !== 'zone');
  const locked = doc.nodes.filter((n) => n.locked).length;
  const cidrs = uniqueCidrs(doc);
  const lifecycle = formatLifecycleForDocument(doc.lifecycle);
  const objectName = meta.objectName.trim() || doc.name || doc.siteId;
  const projectName = meta.projectName.trim() || objectName;
  const parts: string[] = [];

  parts.push(
    `Документ описує логічну топологію мережі обʼєкта «${objectName}» (проєкт «${projectName}», стадія: ${lifecycle.label}). ${lifecycle.description}`,
  );

  const goal =
    meta.purposeGoal.trim() ||
    'зафіксувати склад обладнання, каналів звʼязку та адресного плану для впровадження і подальшого моніторингу';
  parts.push(`Мета: ${goal}.`);

  const audience =
    meta.purposeAudience.trim() ||
    'адміністратори мережі та персонал впровадження / супроводу';
  parts.push(`Призначено для: ${audience}.`);

  if (meta.purposeScope.trim()) {
    parts.push(`Межі схеми: ${meta.purposeScope.trim()}.`);
  }

  const inventory = [
    `${devices.length} од. обладнання`,
    `${zones.length} зон/просторів`,
    `${doc.edges.length} зʼєднань`,
  ];
  if (cidrs.length) inventory.push(`підмережі CIDR: ${cidrs.join(', ')}`);
  parts.push(`На схемі зафіксовано: ${inventory.join('; ')}.`);

  const types = typeSummary(devices);
  if (types) parts.push(`Типи обладнання: ${types}.`);

  if (locked > 0) {
    parts.push(
      `Закріплених елементів: ${locked} (закріплення фіксує положення на полотні й не виключає елемент із креслення чи реєстрів).`,
    );
  }

  parts.push(
    'Після впровадження до цього ж обʼєкта підключається скрипт моніторингу за ключами monitorKey; параметри, яких немає в телеметрії (модель, CIDR, розташування), доповнюються в редакторі схеми.',
  );

  if (meta.notes.trim()) {
    parts.push(`Примітка: ${meta.notes.trim()}.`);
  }

  return parts.join('\n');
}
